import React, { FC, useState, ChangeEvent } from 'react';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { saveInternalOrderIdTemplate } from 'apollo/templates/order';
import { Order } from 'models/order';
import { Box, Button, TextField } from '@mui/material';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type InternalOrderIdDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const InternalOrderIdDialog: FC<InternalOrderIdDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: InternalOrderIdDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [internalOrderId, setInternalOrderId] = useState<string | null>();
    const { showBoundary } = useDenthubErrorBoundary();

    useSeedOnOpen(open, () => {
        if (order?.internalOrderId) {
            setInternalOrderId(order.internalOrderId);
        } else {
            setInternalOrderId(null);
        }
    });

    const [saveInternalOrderId] = useMutation(saveInternalOrderIdTemplate, {
        onCompleted: ({ saveInternalOrderId }) => {
            onClose();
            onUpdate?.({ ...order, ...saveInternalOrderId });
        },
        onError(error) {
            showBoundary(error);
            onClose();
        },
    });

    const handleChangeInternalDeliveryDate = async () => {
        await saveInternalOrderId({
            variables: {
                orderId: order?._id,
                internalOrderId,
            },
        });
    };
    const handleInternalOrderId = (event: ChangeEvent<HTMLInputElement>) => {
        setInternalOrderId(event.target.value);
    };

    const handleClose = () => {
        if (order?.internalOrderId) {
            setInternalOrderId(order.internalOrderId);
        }
        onClose();
    };

    return (
        <>
            <DHDialog
                fullWidth
                maxWidth="sm"
                open={open}
                onClose={handleClose}
                title={translate('orders:INTERNAL_ORDER_ID')}
                actions={
                    <Button
                        disabled={!internalOrderId}
                        variant="contained"
                        onClick={handleChangeInternalDeliveryDate}
                        data-test="internal-order-id-dialog.save-btn"
                    >
                        {translate('common:SAVE')}
                    </Button>
                }
            >
                <Box mt={1}>
                    <TextField
                        fullWidth
                        label={translate('orders:INTERNAL_ORDER_ID')}
                        value={internalOrderId}
                        onChange={handleInternalOrderId}
                        data-test="internal-order-id-dialog.order-id-input"
                    />
                </Box>
            </DHDialog>
        </>
    );
};

export default InternalOrderIdDialog;
